import React, { useState, useEffect } from 'react'
import {
  AlertCircle,
  CheckCircle,
  Clock,
  MessageSquare,
  Filter,
  Search,
  RefreshCw,
  Send,
  User,
  Users,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  feedbackService,
  FeedbackRequest,
  FeedbackRequestType,
  FeedbackRequestStatus,
} from '@/services/api/feedbackService'
import toast from 'react-hot-toast'

const FeedbackRequestsAdmin: React.FC = () => {
  const [requests, setRequests] = useState<FeedbackRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  useEffect(() => {
    loadRequests()
  }, [statusFilter, typeFilter])

  const loadRequests = async () => {
    setLoading(true)
    try {
      const data = await feedbackService.listAllFeedbackRequestsAdmin({
        request_type: typeFilter !== 'all' ? typeFilter as FeedbackRequestType : undefined,
        status: statusFilter !== 'all' ? statusFilter as FeedbackRequestStatus : undefined,
        search: searchTerm || undefined,
        limit: 100,
      })
      setRequests(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Failed to load feedback requests:', error)
      toast.error('Failed to load feedback requests')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    loadRequests()
  }

  const getStatusBadge = (status?: FeedbackRequestStatus) => {
    switch (status) {
      case 'draft':
        return <Badge className="bg-gray-100 text-gray-700 border-gray-200">Draft</Badge>
      case 'pending_approval':
        return <Badge className="bg-amber-100 text-amber-800 border-amber-200"><Clock className="w-3 h-3 mr-1" />Pending Approval</Badge>
      case 'active':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200"><Send className="w-3 h-3 mr-1" />Active</Badge>
      case 'in_progress':
        return <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200"><RefreshCw className="w-3 h-3 mr-1" />In Progress</Badge>
      case 'completed':
        return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Cancelled</Badge>
      case 'expired':
        return <Badge className="bg-gray-100 text-gray-600 border-gray-200">Expired</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  const getTypeBadge = (type: FeedbackRequestType) => {
    const typeLabels: Record<FeedbackRequestType, { label: string; color: string }> = {
      matrix: { label: 'Matrix', color: 'text-purple-700 border-purple-200' },
      upward_360: { label: '360 Upward', color: 'text-blue-700 border-blue-200' },
      employee_requested: { label: 'Team Mbr. Req.', color: 'text-teal-700 border-teal-200' },
      information_checkin: { label: 'Info Check-in', color: 'text-orange-700 border-orange-200' },
      project: { label: 'Project', color: 'text-indigo-700 border-indigo-200' },
    }
    const config = typeLabels[type] || { label: type, color: '' }
    return <Badge variant="outline" className={config.color}>{config.label}</Badge>
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const statusCounts = {
    total: requests.length,
    active: requests.filter(r => r.status === 'active' || r.status === 'in_progress').length,
    pending: requests.filter(r => r.status === 'pending_approval' || r.status === 'draft').length,
    completed: requests.filter(r => r.status === 'completed').length,
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Requests</p>
              <p className="text-2xl font-bold">{statusCounts.total}</p>
            </div>
            <MessageSquare className="h-8 w-8 text-gray-400" />
          </div>
        </Card>
        <Card className="p-4 border-blue-200 bg-blue-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-700">Active / In Progress</p>
              <p className="text-2xl font-bold text-blue-800">{statusCounts.active}</p>
            </div>
            <Send className="h-8 w-8 text-blue-500" />
          </div>
        </Card>
        <Card className="p-4 border-amber-200 bg-amber-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-amber-700">Pending / Draft</p>
              <p className="text-2xl font-bold text-amber-800">{statusCounts.pending}</p>
            </div>
            <Clock className="h-8 w-8 text-amber-500" />
          </div>
        </Card>
        <Card className="p-4 border-green-200 bg-green-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700">Completed</p>
              <p className="text-2xl font-bold text-green-800">{statusCounts.completed}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by title or employee ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_approval">Pending Approval</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="matrix">Matrix</SelectItem>
                <SelectItem value="upward_360">360 Upward</SelectItem>
                <SelectItem value="employee_requested">Team Mbr. Req.</SelectItem>
                <SelectItem value="information_checkin">Info Check-in</SelectItem>
                <SelectItem value="project">Project</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={loadRequests} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </Card>

      {/* Requests List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-heritage-blue" />
            Feedback Requests
            {requests.length > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                ({requests.length} total)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No feedback requests found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map((request) => (
                <div key={request.id} className="border rounded-lg hover:bg-muted/50 transition-colors">
                  <div
                    className="p-4 cursor-pointer"
                    onClick={() => setExpandedId(expandedId === request.id ? null : (request.id ?? null))}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-semibold">{request.title}</span>
                          {getTypeBadge(request.request_type)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            By: {request.requested_by_user_id}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            For: {request.requested_for_user_id}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {request.total_stakeholders ?? request.stakeholders?.length ?? 0} stakeholders
                          </span>
                          {request.responses_received !== undefined && (
                            <span>
                              {request.responses_received}/{request.total_stakeholders ?? request.stakeholders?.length ?? 0} responses
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {getStatusBadge(request.status)}
                        <span className="text-xs text-muted-foreground">
                          {formatDate(request.created_at)}
                        </span>
                        {expandedId === request.id ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedId === request.id && (
                    <div className="px-4 pb-4 border-t">
                      <div className="pt-3 space-y-3">
                        {request.description && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Description</p>
                            <p className="text-sm">{request.description}</p>
                          </div>
                        )}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div>
                            <p className="text-xs text-muted-foreground">Due Date</p>
                            <p className="font-medium">{formatDate(request.due_date)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Anonymous</p>
                            <p className="font-medium">{request.is_anonymous ? 'Yes' : 'No'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Confidential</p>
                            <p className="font-medium">{request.is_confidential ? 'Yes' : 'No'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">AI Analysis</p>
                            <p className="font-medium">{request.ai_analysis_enabled ? 'Enabled' : 'Disabled'}</p>
                          </div>
                        </div>

                        {/* Stakeholders */}
                        {request.stakeholders && request.stakeholders.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-2">
                              Stakeholders ({request.stakeholders.length})
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {request.stakeholders.map((s) => (
                                <div key={s.id || s.stakeholder_user_id} className="flex items-center justify-between p-2 bg-muted/30 rounded text-sm">
                                  <div className="flex items-center gap-2">
                                    <User className="h-3 w-3 text-muted-foreground" />
                                    <span>{s.stakeholder_name}</span>
                                    <Badge variant="outline" className="text-xs">{s.category}</Badge>
                                  </div>
                                  <Badge
                                    className={
                                      s.response_status === 'completed'
                                        ? 'bg-green-100 text-green-700 border-green-200'
                                        : s.response_status === 'pending'
                                        ? 'bg-amber-100 text-amber-700 border-amber-200'
                                        : s.response_status === 'declined'
                                        ? 'bg-red-100 text-red-700 border-red-200'
                                        : 'bg-gray-100 text-gray-700 border-gray-200'
                                    }
                                  >
                                    {s.response_status || 'unknown'}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default FeedbackRequestsAdmin
